import { isNavigationFailure, NavigationFailureType } from 'vue-router';
export const errorHandler = {
    throwNotFound() {
        return 'Failed to Connect to Server (Url Not Found)!';
    },

    throwTimeOut() {
        return 'Connection Time Out!';
    },
    escapeHtml(str) {
        if (!str) return str;
        return str
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    },

    /**
     * Converts raw Kazang / backend error strings into user-friendly messages.
     * Strips Java exception wrappers, transaction IDs, trailing stack frames, and
     * maps known error keywords to safe customer-facing copy.
     */
    formatKazangMessage(raw) {
        if (!raw || typeof raw !== 'string') return raw;

        // Normalize: collapse \n and excess whitespace
        let cleaned = raw.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();

        // Strip the Java exception wrapper the backend re-throws Kazang errors inside,
        // e.g. "java.lang.IllegalStateException: Kazang SignUp failed: status=401, body=Kazang authentication failed: Too many failed logins. Try later"
        // → "Kazang authentication failed: Too many failed logins. Try later"
        cleaned = cleaned.replace(/^java\.[a-zA-Z0-9_.]+Exception:\s*/i, '');
        cleaned = cleaned.replace(/^.*?status\s*=\s*\d+\s*,\s*body\s*=\s*/i, '');

        // Safety net: strip any trailing Java stack frames that may have leaked through.
        // e.g. "...Incorrect username or password at com.chrilantech.lytsoft.services.kazang.KazangPaymentService.process(97) ..."
        // → "...Incorrect username or password"
        cleaned = cleaned.replace(/\s+at\s+com\.chrilantech\.[^\n]*$/i, '');
        cleaned = cleaned.replace(/\s+at\s+(?:java|jdk|sun|org\.springframework)\.[^\n]*$/i, '');
        // Strip common Tomcat / servlet stack frames too (defensive)
        cleaned = cleaned.replace(/\s+at\s+org\.apache\.catalina\.[^\n]*$/i, '');
        cleaned = cleaned.replace(/\s+at\s+org\.apache\.coyote\.[^\n]*$/i, '');
        // Final whitespace cleanup after strip
        cleaned = cleaned.trim();

        // Map known error keywords to friendly messages.
        // Order matters: specific patterns MUST come before the broad "authentication failed" one
        // so that "Incorrect username or password" doesn't get misclassified as a rate-limit.
        const errorMap = [
            { pattern: /insufficient\s*funds/i, message: 'Insufficient funds. Please top up your account and try again.' },
            { pattern: /invalid\s*pin/i, message: 'Invalid PIN entered. Please try again.' },
            { pattern: /pin\s*locked|pin\s*blocked/i, message: 'Your PIN is locked. Please contact your mobile money provider.' },
            { pattern: /subscriber\s*not\s*found|not\s*registered|number\s*not\s*found/i, message: 'This phone number is not registered for mobile money.' },
            { pattern: /timed?\s*out|timeout/i, message: 'The payment request timed out. Please try again.' },
            { pattern: /transaction\s*limit|limit\s*exceeded|exceed/i, message: 'Transaction limit exceeded. Please try a smaller amount.' },
            { pattern: /service\s*unavailable|system\s*error|system\s*failure/i, message: 'The payment service is temporarily unavailable. Please try again later.' },
            { pattern: /invalid\s*amount/i, message: 'Invalid payment amount. Please check and try again.' },
            { pattern: /duplicate\s*transaction/i, message: 'This transaction has already been processed.' },
            { pattern: /account\s*blocked|account\s*suspended/i, message: 'This mobile money account is blocked. Please contact your provider.' },
            // BE-003: Kazang re-thrown auth / signup failures.
            // Specific credential failures must come BEFORE the broad "authentication failed" pattern.
            { pattern: /incorrect\s*username|invalid\s*credentials|wrong\s*credentials|invalid\s*username|invalid\s*password|wrong\s*password/i, message: 'Payment-service credentials are incorrect. Please contact your supervisor or Chrilan support.' },
            // Specific rate-limit pattern (no "authentication failed" prefix)
            { pattern: /too\s*many.*failed\s*logins|too\s*many\s*attempts|too\s*many\s*login/i, message: 'Too many login attempts. Please wait a few minutes and try again.' },
            { pattern: /kazang\s*signup\s*failed|signup\s*failed/i, message: 'Device signup failed. Please contact support to register this device.' },
            // Broad auth failure (last resort — most "authentication failed" cases are credentials, not rate-limit)
            { pattern: /authentication\s*failed/i, message: 'Could not authenticate with the payment service. Please try again in a few minutes.' },
        ];

        for (const { pattern, message } of errorMap) {
            if (pattern.test(cleaned)) return message;
        }

        // Fallback: if it contains FAILED but no known keyword, give a generic message
        if (/failed/i.test(cleaned)) {
            return 'Payment was not successful. Please try again.';
        }

        // Final safety net: if any Java-stack markers, Java reflection error fragments, or
        // Tomcat / Catalina / Coyote frame names remain, don't leak the raw trace to the
        // customer. Covers BE-001 (NullPointerException), BE-002 (Tomcat-only frames), and
        // any other 500 with a Java-side exception we haven't explicitly mapped.
        if (/at\s+com\.chrilantech\.|at\s+java\.|at\s+jdk\.|at\s+org\.apache\.|cannot\s+(invoke|resolve|cast|find|load|create|allocate|access)|org\.apache\.(catalina|coyote|tomcat)\./i.test(cleaned)) {
            return 'A server error occurred. Please try again, or contact support if the problem persists.';
        }

        return cleaned;
    },

    extractErrorMessage(response) {
        if (!response || typeof response !== "object") return response;

        let errorMessage =
            response.error ||
            response.Error ||
            response.response_message ||
            response.message ||
            response.Message ||
            (response.Print?.toString().includes("failed") ? response.Print : JSON.stringify(response));

        if (response.response && response.response.Message) {
            errorMessage = response.response.Message;
        }

        return this.escapeHtml(errorMessage);
    },

    franchiseLoginError(err) {
        // Ignore benign router navigation failures (redirected/duplicated/aborted)
        try {
            if (isNavigationFailure(err, NavigationFailureType.redirected) ||
                isNavigationFailure(err, NavigationFailureType.duplicated) ||
                isNavigationFailure(err, NavigationFailureType.aborted)) {
                return; // swallow benign router navigation rejections
            }
        } catch (_) { /* not a router error */ }
        let errorMessage = err;

        if (err.response) {
            console.error(err.response);
            errorMessage = err.response.status === 404 ? this.throwNotFound() : this.extractErrorMessage(err.response.data);
        } else if (err.code === "ECONNABORTED") {
            errorMessage = this.throwTimeOut();
        } else {
            console.error(err);
            errorMessage = err.message;
        }

        if (errorMessage === "Network Error") {
            errorMessage = "Please check your internet connection.";
        }

        return errorMessage;
    },

    tomcatError(err, isSweetalert = false, title = "An Error Occurred") {
        // Ignore benign router navigation failures (redirected/duplicated/aborted)
        try {
            if (isNavigationFailure(err, NavigationFailureType.redirected) ||
                isNavigationFailure(err, NavigationFailureType.duplicated) ||
                isNavigationFailure(err, NavigationFailureType.aborted)) {
                return; // swallow benign router navigation rejections
            }
        } catch (_) { /* not a router error */ }
        let errorMessage = err;
        let response = err.response;

        if (err.response) {
            console.error(err.response);
            if (err.response.status === 404) {
                errorMessage = this.throwNotFound();
            }
            else if(err.response.status === 503){
                let serverError = err.response.data;
                if (typeof serverError === 'object') {
                    serverError = serverError.message || serverError.error || serverError.Error ||
                        serverError.response_message || JSON.stringify(serverError);
                }
                return Swal.fire({
                    title: 'Timeout Error Please try again',
                    icon: `warning`,
                    html : `<span>${serverError}</span>`,
                    showCloseButton : true,
                })
            }
            else if (err.response.status === 500) {
                let serverError = this.extractErrorMessage(err.response.data);
                // Route any 500 that smells like a backend / Kazang error through the
                // friendly-message formatter so the customer never sees a raw Java stack.
                // BE-002: Tomcat 500s for non-Kazang backend errors must also be sanitized.
                if (typeof serverError === 'string' && (
                    /kazang/i.test(serverError) ||
                    /com\.chrilantech\./i.test(serverError) ||
                    /java\.lang\.[A-Z]\w*Exception/i.test(serverError)
                )) {
                    serverError = this.formatKazangMessage(serverError);
                }
                return Swal.fire({
                    title,
                    icon: "error",
                    html: `<p>Please contact support: +260958168829</p><span>${serverError}</span>`,
                    showCloseButton: true,
                });
            } else {
                errorMessage = this.extractErrorMessage(err.response.data);
            }
        } else if (err.code === "ECONNABORTED") {
            errorMessage = this.throwTimeOut();
        } else {
            console.error(err);
            errorMessage = err.message;
        }

        if (errorMessage === "Network Error") {
            errorMessage = "Please check your internet connection.";
        }

        if (!isSweetalert) {
            responses.throwErr(errorMessage, title);
        }

        throw new Error(errorMessage);
    },

    customerKazangError(err, isSweetalert = false) {
        console.log(err);
        let errorMessage = err;

        if (err.response) {
            console.error(err.response);
            errorMessage = err.response.status === 404 ? this.throwNotFound() : this.extractErrorMessage(err.response.data);
        } else if (err.Error) {
            errorMessage = err.Message;
        } else if (err.code === "ECONNABORTED") {
            errorMessage = this.throwTimeOut();
        }

        if (!isSweetalert) {
            responses.throwErr(errorMessage);
        }

        throw new Error(errorMessage);
    },
};